import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import FabBar from "@/design/components/FabBar";
import MasterListVirtualized from "@/design/components/MasterListVirtualized";
import { disableSearch, enableSearch } from "@/store/reducers/ui";
import useSearch from "@/utils/hooks/useSearch";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viPeopleCircle, viPlus } from "@sippy-platform/valkyrie";

import { useMediaQuery, useTheme } from "@mui/material";
import Button from "@mui/material/Button";

function ConferenceList() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const matchesMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const conferenceStore = useSelector((state) => state.domain.conferences);
  const searchValue = useSelector((state) => state.ui.searchValue);
  const progressBar = useSelector((state) => state.ui.progressBar);

  // Search every time conferenceList or searchQuery changes
  const searchResults = useSearch(conferenceStore.list, searchValue, ["name", "exten"]);

  useEffect(() => {
    toggleLoadingState(true);
    Promise.resolve(Api.getConferences()).finally(toggleLoadingState);
  }, []);

  // Open first item or new if the viewport is large enough
  useEffect(() => {
    if (!id && matchesMdUp && conferenceStore.meta.lastFetched) {
      if (conferenceStore.list.length >= 1) {
        navigate(`${conferenceStore.list[0].id}`);
      } else {
        navigate("new");
      }
    }
  }, [id, matchesMdUp, navigate, conferenceStore.list, conferenceStore.meta.lastFetched]);

  useEffect(() => {
    dispatch(enableSearch());
  }, [dispatch]);
  useEffect(() => () => dispatch(disableSearch()), [dispatch]);

  return (
    <>
      <MasterListVirtualized
        fabBar
        title="Conferences"
        aria-label="Conference list"
        id="conference-list"
        list={searchResults}
        listItemProps={{
          primary: (x) => x.name,
          secondary: (x) => x.exten,
          to: (x) => `../${x.id}`,
          selected: (x) => x.id === Number(id),
          icon: viPeopleCircle,
        }}
        hideContentLoader={conferenceStore.meta.count >= 1 && searchResults.length >= 1}
        contentLoaderProps={{
          count: conferenceStore.meta.count,
          loading: progressBar,
          noItems: "You don't have any conferences yet",
          noResults: "No conferences in your current query",
          fetching: "Getting your conferences",
          icon: viPeopleCircle,
        }}
      />
      <FabBar>
        <Button variant="contained" component={NavLink} to="new" startIcon={<Valkyrie icon={viPlus} />}>
          Add
        </Button>
      </FabBar>
    </>
  );
}

export default ConferenceList;
